import { maxBy } from 'lodash-es';

export const COST_CONTROLLING_TAB_IDS = ['general-dashboard', 'fee-check-dashboard', 'cost-tracking'] as const;

export type CostControllingTabId = (typeof COST_CONTROLLING_TAB_IDS)[number];

export interface CostControllingProjectSummary {
    mfhRenovationProjectId?: number | null;
    isPrimaryRenovationProject?: boolean | null;
    mfhRenovationProjectCostBreakdowns?: CostControllingCostBreakdownSummary[] | null;
}

export interface CostControllingCostBreakdownSummary {
    mfhRenovationProjectCostBreakdownId?: number | null;
    createdAt?: string | null;
}

export function isCostControllingTabId(value: string): value is CostControllingTabId {
    return (COST_CONTROLLING_TAB_IDS as readonly string[]).includes(value);
}

export function normalizeCostControllingTab(value: unknown): CostControllingTabId {
    return typeof value === 'string' && isCostControllingTabId(value) ? value : 'general-dashboard';
}

export function getDefaultMfhRenovationProjectId(projects: CostControllingProjectSummary[]): number | null {
    const primaryProject = projects.find(project => project.isPrimaryRenovationProject && typeof project.mfhRenovationProjectId === 'number');
    if (primaryProject?.mfhRenovationProjectId != null) {
        return primaryProject.mfhRenovationProjectId;
    }

    return (
        maxBy(
            projects.filter(
                (project): project is CostControllingProjectSummary & { mfhRenovationProjectId: number } => typeof project.mfhRenovationProjectId === 'number',
            ),
            'mfhRenovationProjectId',
        )?.mfhRenovationProjectId ?? null
    );
}

export function getDefaultCostBreakdownId(projects: CostControllingProjectSummary[], mfhRenovationProjectId: number | null): number | null {
    if (mfhRenovationProjectId == null) {
        return null;
    }

    const project = projects.find(candidate => candidate.mfhRenovationProjectId === mfhRenovationProjectId);
    const latestBreakdown = maxBy(
        (project?.mfhRenovationProjectCostBreakdowns ?? []).filter(
            (breakdown): breakdown is CostControllingCostBreakdownSummary & { mfhRenovationProjectCostBreakdownId: number } =>
                typeof breakdown.mfhRenovationProjectCostBreakdownId === 'number',
        ),
        breakdown => new Date(breakdown.createdAt ?? 0).getTime(),
    );

    return latestBreakdown?.mfhRenovationProjectCostBreakdownId ?? null;
}
